import React, { useState, useContext, useEffect } from "react";

//pages & includes
import ManageSidebar from "../ManageSidebar";
//functions
import {
  _t,
  getCookie,
  pagination,
  showingData,
  searchedShowingData,
} from "../../../../../functions/Functions";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { BASE_URL } from "../../../../../BaseUrl";

//3rd party packages
import { Helmet } from "react-helmet";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MdDelete } from "react-icons/md";
import { SettingsContext } from "../../../../../contexts/Settings";
import { RestaurantContext } from "../../../../../contexts/Restaurant";
import CustomSpinLoader from "../../../../components/CustomSpinLoader";

const ensurePaginationObject = (pag, list) => {
  const data = Array.isArray(pag?.data)
    ? pag.data
    : Array.isArray(list)
      ? list
      : [];
  const total = pag?.total !== undefined ? pag.total : data.length;
  const per_page = pag?.per_page || (data.length > 0 ? data.length : 10);
  const current_page = pag?.current_page || 1;
  const last_page =
    pag?.last_page || Math.max(1, Math.ceil(total / (per_page || 10)));
  const from =
    pag?.from !== undefined
      ? pag.from
      : data.length > 0
        ? (current_page - 1) * per_page + 1
        : 0;
  const to =
    pag?.to !== undefined
      ? pag.to
      : data.length > 0
        ? Math.min(total, from + data.length - 1)
        : 0;

  return {
    ...pag,
    data,
    total,
    per_page,
    current_page,
    last_page,
    from,
    to,
  };
};

const extractAreaListAndPagination = (resData) => {
  if (!resData) {
    return {
      list: [],
      paginationData: ensurePaginationObject({ data: [] }, []),
    };
  }

  let list = [];
  let paginationData = null;

  // API response: [paginationObject, areaArray]
  if (
    Array.isArray(resData) &&
    resData.length >= 2 &&
    resData[0]?.data &&
    Array.isArray(resData[1])
  ) {
    list = resData[1];
    paginationData = resData[0];
  } else if (Array.isArray(resData)) {
    list = resData;
    paginationData = { data: resData };
  } else if (resData.code === 1 && resData.details) {
    list = resData.details.areaList || resData.details.pagination?.data || [];
    paginationData = resData.details.pagination || { data: list };
  } else if (Array.isArray(resData.data)) {
    list = resData.data;
    paginationData = resData;
  } else if (resData.data && Array.isArray(resData.data.data)) {
    list = resData.data.data;
    paginationData = resData.data;
  } else if (Array.isArray(resData.areas)) {
    list = resData.areas;
    paginationData = { data: resData.areas };
  } else if (
    resData.details?.areaList &&
    Array.isArray(resData.details.areaList)
  ) {
    list = resData.details.areaList;
    paginationData = { data: resData.details.areaList };
  } else {
    list = [];
    paginationData = { data: [] };
  }

  return {
    list,
    paginationData: ensurePaginationObject(paginationData, list),
  };
};

const Areas = () => {
  const { t } = useTranslation();
  let { loading, setLoading } = useContext(SettingsContext);
  const restaurantContext = useContext(RestaurantContext);
  const setRestaurantAreaForSearch = restaurantContext?.setAreaForSearch;

  const [areaList, setAreaList] = useState({
    data: [],
    total: 0,
    from: 0,
    to: 0,
    current_page: 1,
    last_page: 1,
    per_page: 10,
  });
  const [areaForSearch, setAreaForSearch] = useState(null);
  const [dataPaginating, setDataPaginating] = useState(false);

  //search result
  let [searched, setSearched] = useState({
    list: null,
    searched: false,
  });

  let [newArea, setNewArea] = useState({
    name: "",
    sequence: 1,
    edit: false,
    editSlug: null,
    uploading: false,
  });

  const getAreas = async () => {
    setLoading(true);
    const url = BASE_URL + "/settings/areas";
    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      if (res.data) {
        if (res.data.code === 2) {
          toast.error(
            res.data.message || `${_t(t("Call Center is not enabled"))}`,
            {
              position: "bottom-center",
              autoClose: 10000,
              className: "text-center toast-notification",
            },
          );
        }
        const { list, paginationData } = extractAreaListAndPagination(res.data);
        setAreaList(paginationData);
        setAreaForSearch(list);
        if (setRestaurantAreaForSearch && Array.isArray(list)) {
          setRestaurantAreaForSearch(list);
        }
      } else {
        setAreaList({ data: [] });
        setAreaForSearch([]);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching areas from /settings/areas:", error);
      setAreaList({ data: [] });
      setAreaForSearch([]);
      setLoading(false);
    }
  };

  const setPaginatedAreas = async (pageNo) => {
    setDataPaginating(true);
    const url = BASE_URL + "/settings/areas?page=" + pageNo;
    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      if (res.data) {
        const { list, paginationData } = extractAreaListAndPagination(res.data);
        setAreaList(paginationData);
        setAreaForSearch(list);
      }
      setDataPaginating(false);
    } catch (error) {
      setDataPaginating(false);
    }
  };

  useEffect(() => {
    getAreas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e) => {
    let searchInput = e.target.value.toLowerCase();
    if (searchInput.length === 0) {
      setSearched({ ...searched, searched: false });
    } else {
      let searchedList = (areaForSearch || []).filter((area) => {
        let lowerCaseAreaName = (
          area.name ||
          area.area_name ||
          area.area ||
          area.title ||
          ""
        ).toLowerCase();
        let seq = String(area.sequence || "");
        return (
          lowerCaseAreaName.includes(searchInput) || seq.includes(searchInput)
        );
      });
      setSearched({
        ...searched,
        list: searchedList,
        searched: true,
      });
    }
  };

  //delete confirmation modal
  const handleDeleteConfirmation = (id) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="card card-body">
            <h1>{_t(t("Are you sure?"))}</h1>
            <p className="text-center">{_t(t("You want to delete this?"))}</p>
            <div className="d-flex justify-content-center">
              <button
                className="btn btn-primary rounded"
                onClick={() => {
                  handleDeleteArea(id);
                  onClose();
                }}
              >
                {_t(t("Yes, delete it!"))}
              </button>
              <button
                className="btn btn-success ml-2 px-3 rounded"
                onClick={onClose}
              >
                {_t(t("No"))}
              </button>
            </div>
          </div>
        );
      },
    });
  };

  //delete area
  // const handleDeleteArea = (id) => {
  //   setLoading(true);
  //   const areaUrl = BASE_URL + `/settings/areas/${id}`;
  //   return axios
  //     .delete(areaUrl, {
  //       headers: { Authorization: `Bearer ${getCookie()}` },
  //     })
  //     .then(() => {
  //       setLoading(false);
  //       toast.success(`${_t(t("Area has been deleted successfully"))}`, {
  //         position: "bottom-center",
  //         autoClose: 10000,
  //         hideProgressBar: false,
  //         closeOnClick: true,
  //         pauseOnHover: true,
  //         className: "text-center toast-notification",
  //       });
  //       getAreas();
  //     })
  //     .catch((error) => {
  //       setLoading(false);
  //       const errorMsg = error.response?.data?.message || _t(t("Please try again"));
  //       toast.error(`${errorMsg}`, {
  //         position: "bottom-center",
  //         autoClose: 10000,
  //         hideProgressBar: false,
  //         closeOnClick: true,
  //         pauseOnHover: true,
  //         className: "text-center toast-notification",
  //       });
  //     });
  // };
  const handleDeleteArea = (id) => {
    setLoading(true);

    const areaUrl = BASE_URL + `/settings/areas/${id}`;

    axios
      .delete(areaUrl, {
        headers: {
          Authorization: `Bearer ${getCookie()}`,
        },
      })
      .then(() => {
        setLoading(false);

        // Update list without calling GET API
        setAreaList((prev) => ({
          ...prev,
          data: prev.data.filter((item) => {
            const itemId = item.area_id;
            return itemId !== id;
          }),
          total: Math.max(0, (prev.total || 0) - 1),
        }));

        toast.success(`${_t(t("Area has been deleted successfully"))}`, {
          position: "bottom-center",
          autoClose: 10000,
          className: "text-center toast-notification",
        });
      })
      .catch((error) => {
        setLoading(false);

        const errorMsg =
          error.response?.data?.message || _t(t("Please try again"));

        toast.error(`${errorMsg}`, {
          position: "bottom-center",
          autoClose: 10000,
          className: "text-center toast-notification",
        });
      });
  };
  const handleOpenAdd = () => {
    setNewArea({
      name: "",
      sequence: 1,
      edit: false,
      editSlug: null,
      uploading: false,
    });
  };

  const handleSetEdit = (item) => {
    const areaId = item.area_id;
    setNewArea({
      name: item.name || item.area_name || item.area || item.title || "",
      sequence:
        item.sequence !== undefined && item.sequence !== null
          ? item.sequence
          : 1,
      edit: true,
      editSlug: areaId,
      uploading: false,
    });

    if (areaId) {
      axios
        .get(BASE_URL + `/settings/areas/${areaId}`, {
          headers: { Authorization: `Bearer ${getCookie()}` },
        })
        .then((res) => {
          const detail =
            res.data?.details?.area ||
            res.data?.details ||
            res.data?.data ||
            res.data;
          if (
            detail &&
            (detail.name || detail.area_name || detail.sequence !== undefined)
          ) {
            setNewArea((prev) => ({
              ...prev,
              name:
                detail.name ||
                detail.area_name ||
                detail.area ||
                detail.title ||
                prev.name,
              sequence:
                detail.sequence !== undefined && detail.sequence !== null
                  ? detail.sequence
                  : prev.sequence,
            }));
          }
        })
        .catch(() => {});
    }
  };

  const handleAreaChange = (e) => {
    setNewArea({
      ...newArea,
      [e.target.name]: e.target.value,
    });
  };

  const closeModal = () => {
    const closeBtn = document.getElementById("close-area-modal");
    if (closeBtn) {
      closeBtn.click();
    }
  };

  const handleSaveNewArea = (e) => {
    e.preventDefault();
    if (!newArea.name || newArea.name.trim() === "") {
      toast.error(`${_t(t("Please enter area name"))}`, {
        position: "bottom-center",
        autoClose: 10000,
        hideProgressBar: false,
        pauseOnHover: true,
        className: "text-center toast-notification",
      });
      return;
    }

    setNewArea({
      ...newArea,
      uploading: true,
    });

    const areaUrl = BASE_URL + "/settings/areas";
    const formData = {
      name: newArea.name.trim(),
      sequence: newArea.sequence !== "" ? parseInt(newArea.sequence, 10) : 0,
    };

    return axios
      .post(areaUrl, formData, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      })
      .then(() => {
        setNewArea({
          name: "",
          sequence: 1,
          edit: false,
          editSlug: null,
          uploading: false,
        });
        closeModal();
        toast.success(`${_t(t("Area has been added"))}`, {
          position: "bottom-center",
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          className: "text-center toast-notification",
        });
        getAreas();
      })
      .catch((error) => {
        console.log("check error==>", error);
        setNewArea({
          ...newArea,
          uploading: false,
        });
        const errorMsg =
          error.response?.data?.message || _t(t("Please try again"));
        toast.error(`${errorMsg}`, {
          position: "bottom-center",
          autoClose: 10000,
          hideProgressBar: false,
          pauseOnHover: true,
          className: "text-center toast-notification",
        });
      });
  };

  const handleUpdateArea = (e) => {
    e.preventDefault();
    if (!newArea.name || newArea.name.trim() === "") {
      toast.error(`${_t(t("Please enter area name"))}`, {
        position: "bottom-center",
        autoClose: 10000,
        hideProgressBar: false,
        pauseOnHover: true,
        className: "text-center toast-notification",
      });
      return;
    }

    setNewArea({
      ...newArea,
      uploading: true,
    });

    const areaUrl = BASE_URL + `/settings/areas/${newArea.editSlug}`;
    const formData = {
      name: newArea.name.trim(),
      sequence: newArea.sequence !== "" ? parseInt(newArea.sequence, 10) : 0,
    };

    return axios
      .put(areaUrl, formData, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      })
      .then(() => {
        setNewArea({
          name: "",
          sequence: 1,
          edit: false,
          editSlug: null,
          uploading: false,
        });
        closeModal();
        toast.success(`${_t(t("Area has been updated"))}`, {
          position: "bottom-center",
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          className: "text-center toast-notification",
        });
        getAreas();
      })
      .catch((error) => {
        console.log("check error==>", error);
        setNewArea({
          ...newArea,
          uploading: false,
        });
        const errorMsg =
          error.response?.data?.message || _t(t("Please try again"));
        toast.error(`${errorMsg}`, {
          position: "bottom-center",
          autoClose: 10000,
          hideProgressBar: false,
          pauseOnHover: true,
          className: "text-center toast-notification",
        });
      });
  };

  return (
    <div>
      <Helmet>
        <title>{_t(t("Areas"))}</title>
      </Helmet>

      {/* Add / Edit modal */}
      <div className="modal fade" id="addAreaModal" aria-hidden="true">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header align-items-center">
              <div className="fk-sm-card__content">
                <h5 className="text-capitalize fk-sm-card__title">
                  {!newArea.edit ? _t(t("Add New Area")) : _t(t("Update Area"))}
                </h5>
              </div>
              <button
                type="button"
                id="close-area-modal"
                className="btn-close"
                data-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              {newArea.uploading === false ? (
                <form
                  onSubmit={
                    !newArea.edit ? handleSaveNewArea : handleUpdateArea
                  }
                  autoComplete="off"
                >
                  <div className="mb-3">
                    <label htmlFor="area-name" className="form-label">
                      {_t(t("Area Name"))}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="area-name"
                      name="name"
                      placeholder="e.g. Malir"
                      value={newArea.name || ""}
                      required
                      onChange={handleAreaChange}
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="area-sequence" className="form-label">
                      {_t(t("Sequence"))} <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="area-sequence"
                      name="sequence"
                      placeholder="e.g. 1"
                      value={newArea.sequence}
                      min="0"
                      required
                      onChange={handleAreaChange}
                    />
                  </div>

                  <div className="mt-4">
                    <div className="row d-flex justify-content-end align-items-center">
                      <div className="col-4">
                        <button
                          type="submit"
                          className="btn btn-success w-100 xsm-text text-uppercase t-width-max rounded"
                        >
                          {!newArea.edit ? _t(t("Save")) : _t(t("Update"))}
                        </button>
                      </div>
                      <div className="col-4">
                        <button
                          type="button"
                          className="btn btn-primary w-100 xsm-text text-uppercase t-width-max rounded"
                          data-dismiss="modal"
                        >
                          {_t(t("Close"))}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              ) : (
                <div className="text-center py-4">
                  <CustomSpinLoader loading={true} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ManageSidebar />
            </div>
            {/* left Sidebar ends */}

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div className="fk-scroll--pos-table-menu" data-simplebar>
                  <div className="t-pl-15 t-pr-15">
                    <div key="fragment3">
                      {/* next page data spin loading */}
                      <CustomSpinLoader loading={loading || dataPaginating} />
                      {/* spin loading ends */}

                      <div className="row gx-2 align-items-center t-pt-15 t-pb-15">
                        <div className="col-md-6 col-lg-5 t-mb-15 mb-md-0">
                          <ul className="t-list fk-breadcrumb">
                            <li className="fk-breadcrumb__list">
                              <span className="t-link fk-breadcrumb__link text-capitalize">
                                {!searched.searched
                                  ? _t(t("Areas List"))
                                  : _t(t("Search Result"))}
                              </span>
                            </li>
                          </ul>
                        </div>
                        <div className="col-md-6 col-lg-7">
                          <div className="row gx-3 align-items-center">
                            <div className="col-md-9 t-mb-15 mb-md-0">
                              <div className="input-group">
                                <div className="form-file">
                                  <input
                                    type="text"
                                    className="form-control border-0 form-control--light-1 rounded-0"
                                    placeholder={_t(t("Search")) + ".."}
                                    onChange={handleSearch}
                                  />
                                </div>
                                <button
                                  className="btn btn-primary"
                                  type="button"
                                >
                                  <i
                                    className="fa fa-search"
                                    aria-hidden="true"
                                  ></i>
                                </button>
                              </div>
                            </div>

                            <div className="col-md-3 text-md-right">
                              <button
                                type="button"
                                className="btn btn-primary xsm-text text-uppercase btn-lg btn-block rounded"
                                data-toggle="modal"
                                data-target="#addAreaModal"
                                onClick={handleOpenAdd}
                              >
                                {_t(t("add new"))}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Table */}
                      <div className="table-responsive">
                        <table className="table table-bordered table-hover min-table-height">
                          <thead className="align-middle">
                            <tr>
                              <th
                                scope="col"
                                className="sm-text text-capitalize align-middle text-center border-1 border"
                              >
                                {_t(t("S/L"))}
                              </th>

                              <th
                                scope="col"
                                className="sm-text text-capitalize align-middle text-center border-1 border"
                              >
                                {_t(t("Area"))}
                              </th>

                              <th
                                scope="col"
                                className="sm-text text-capitalize align-middle text-center border-1 border"
                              >
                                {_t(t("Sequence"))}
                              </th>

                              <th
                                scope="col"
                                className="sm-text text-capitalize align-middle text-center border-1 border"
                              >
                                {_t(t("Action"))}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="align-middle">
                            {!searched.searched ? (
                              !loading &&
                              areaList &&
                              (!areaList.data || areaList.data.length === 0) ? (
                                <tr className="align-middle" key="empty-row">
                                  <td
                                    colSpan="4"
                                    className="xsm-text align-middle text-center"
                                  >
                                    {_t(t("No data available"))}
                                  </td>
                                </tr>
                              ) : (
                                areaList &&
                                areaList.data &&
                                areaList.data.map((item, index) => {
                                  const areaId = item.area_id;
                                  return (
                                    <tr className="align-middle" key={index}>
                                      <th
                                        scope="row"
                                        className="xsm-text text-capitalize align-middle text-center"
                                      >
                                        {index +
                                          1 +
                                          ((areaList.current_page || 1) - 1) *
                                            (areaList.per_page ||
                                              areaList.data.length)}
                                      </th>

                                      <td className="xsm-text text-capitalize align-middle text-center">
                                        {item.name ||
                                          item.area_name ||
                                          item.area ||
                                          item.title ||
                                          "-"}
                                      </td>

                                      <td className="xsm-text text-capitalize align-middle text-center">
                                        {item.sequence !== undefined &&
                                        item.sequence !== null
                                          ? item.sequence
                                          : "-"}
                                      </td>

                                      <td className="xsm-text text-capitalize align-middle text-center">
                                        <button
                                          type="button"
                                          className="btn btn-primary ml-2 rounded-sm"
                                          data-toggle="modal"
                                          data-target="#addAreaModal"
                                          onClick={() => handleSetEdit(item)}
                                          style={{
                                            height: "1.7rem",
                                            width: "1.7rem",
                                          }}
                                        >
                                          <div className="d-flex align-items-center justify-content-center">
                                            <i
                                              className="fa fa-pencil edit-icon"
                                              aria-hidden="true"
                                            ></i>
                                          </div>
                                        </button>
                                        <button
                                          className="btn btn-danger ml-2 rounded-sm del-btn"
                                          onClick={() =>
                                            handleDeleteConfirmation(areaId)
                                          }
                                        >
                                          <MdDelete className="del-icon" />
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })
                              )
                            ) : !searched.list || searched.list.length === 0 ? (
                              <tr
                                className="align-middle"
                                key="empty-search-row"
                              >
                                <td
                                  colSpan="4"
                                  className="xsm-text align-middle text-center"
                                >
                                  {_t(t("No data available"))}
                                </td>
                              </tr>
                            ) : (
                              searched.list.map((item, index) => {
                                const areaId = item.area_id;
                                return (
                                  <tr className="align-middle" key={index}>
                                    <th
                                      scope="row"
                                      className="xsm-text text-capitalize align-middle text-center"
                                    >
                                      {index + 1}
                                    </th>

                                    <td className="xsm-text text-capitalize align-middle text-center">
                                      {item.name ||
                                        item.area_name ||
                                        item.area ||
                                        item.title ||
                                        "-"}
                                    </td>

                                    <td className="xsm-text text-capitalize align-middle text-center">
                                      {item.sequence !== undefined &&
                                      item.sequence !== null
                                        ? item.sequence
                                        : "-"}
                                    </td>

                                    <td className="xsm-text text-capitalize align-middle text-center">
                                      <button
                                        type="button"
                                        className="btn btn-primary ml-2 rounded-sm"
                                        data-toggle="modal"
                                        data-target="#addAreaModal"
                                        onClick={() => handleSetEdit(item)}
                                        style={{
                                          height: "1.7rem",
                                          width: "1.7rem",
                                        }}
                                      >
                                        <div className="d-flex align-items-center justify-content-center">
                                          <i
                                            className="fa fa-pencil edit-icon"
                                            aria-hidden="true"
                                          ></i>
                                        </div>
                                      </button>
                                      <button
                                        className="btn btn-danger ml-2 rounded-sm del-btn"
                                        onClick={() =>
                                          handleDeleteConfirmation(areaId)
                                        }
                                      >
                                        <MdDelete className="del-icon" />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* pagination */}
              {!searched.searched ? (
                <div key="fragment4">
                  <div className="t-bg-white mt-1 t-pt-5 t-pb-5">
                    <div className="row align-items-center t-pl-15 t-pr-15">
                      <div className="col-md-7 t-mb-15 mb-md-0">
                        {pagination(areaList, setPaginatedAreas)}
                      </div>
                      <div className="col-md-5">
                        <ul className="t-list d-flex justify-content-md-end align-items-center">
                          <li className="t-list__item">
                            <span className="d-inline-block sm-text">
                              {showingData(areaList)}
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="t-bg-white mt-1 t-pt-5 t-pb-5">
                  <div className="row align-items-center t-pl-15 t-pr-15">
                    <div className="col-md-7 t-mb-15 mb-md-0">
                      <ul className="t-list d-flex">
                        <li className="t-list__item no-pagination-style">
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() =>
                              setSearched({
                                ...searched,
                                searched: false,
                              })
                            }
                          >
                            {_t(t("Clear Search"))}
                          </button>
                        </li>
                      </ul>
                    </div>
                    <div className="col-md-5">
                      <ul className="t-list d-flex justify-content-md-end align-items-center">
                        <li className="t-list__item">
                          <span className="d-inline-block sm-text">
                            {searchedShowingData(searched, areaForSearch)}
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Areas;
